import type {
  Cell,
  Degree,
  Level,
  Observatory,
  Ring,
  ScaleId,
} from '@astrasonora/annotation-schema'
import { NATURAL, STARS, cellId, isStar, parseCell } from '@astrasonora/annotation-schema'
import { diatonicQuality, scaleOf } from './scales.ts'

export type Accidental = '' | 'b' | '#'

export const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'] as const

/** The degree 0..6 a Roman numeral names, either case; undefined for anything else. */
export function romanDegree(numeral: string): number | undefined {
  const i = (ROMAN as readonly string[]).indexOf(numeral.toUpperCase())
  return i < 0 ? undefined : i
}
const ACC_SYMBOL: Record<Accidental, string> = { '': '', b: '♭', '#': '♯' }

/** Night names, the hours of the night (spec §5). */
const NIGHT_NAMES: readonly string[] = [
  'Dusk',
  'Nightfall',
  'Moonrise',
  'Full dark',
  'Midnight',
  'Small hours',
  'Before dawn',
]

export function nightName(level: Level): string {
  return NIGHT_NAMES[level - 1]!
}

/**
 * The observatory that charts a declared scale (spec §1): a mode is its parent with one note
 * pushed, so the modes with the minor third belong to the minor observatory, the others to the major.
 */
export function observatoryOf(scale: ScaleId): Observatory {
  return scale === 'aeolian' || scale === 'dorian' || scale === 'phrygian' ? 'minor' : 'major'
}

/** The home star of a scale (visual §2): the tonic's own triad, I in a major song, i in a minor one. */
export function homeCell(scale: ScaleId): Cell {
  const q = diatonicQuality(0, scaleOf(scale))
  return cellId(0, q === 'min' ? 'min' : 'maj')
}

/** The observatory's name in the world (visual §7): `Major Observatory`, `Minor Observatory`. */
export function observatoryName(o: Observatory): string {
  return o === 'major' ? 'Major Observatory' : 'Minor Observatory'
}

/** The short name the night's sentence uses after the first mention: `the Major`, `the Minor`. */
export function observatoryShort(o: Observatory): string {
  return o === 'major' ? 'Major' : 'Minor'
}

/**
 * Spelling of a root, counted from the major scale whatever the song's mode (spec §3 `spell`): a
 * natural spoke carries its plain numeral; a chromatic root is spelled from above with a flat,
 * except a diminished triad, a leading-tone chord, which is spelled from below with a sharp.
 */
export function spell(rel: number, ring: Ring): { degree: Degree; acc: Accidental } {
  const r = ((rel % 12) + 12) % 12
  const natural = NATURAL.indexOf(r)
  if (natural >= 0) return { degree: natural as Degree, acc: '' }
  if (ring === 'dim') return { degree: NATURAL.indexOf(r - 1) as Degree, acc: '#' }
  return { degree: NATURAL.indexOf((r + 1) % 12) as Degree, acc: 'b' }
}

/** The numeral of a letter degree in the case of a ring, with its accidental and symbol. */
export function numeralOf(degree: Degree, acc: Accidental, ring: Ring): string {
  const r = ROMAN[degree]!
  return ACC_SYMBOL[acc] + (ring === 'maj' ? r : r.toLowerCase()) + (ring === 'dim' ? '°' : '')
}

/** The triad's own numeral of a cell: `♭III`, `vii°`, `♯iv°`, `i`. */
export function cellName(cell: Cell): string {
  const { rel, ring } = parseCell(cell)
  const { degree, acc } = spell(rel, ring)
  return numeralOf(degree, acc, ring)
}

/** The letter degree a cell's numeral is written with (its colour, spec §4). */
export function letterOf(cell: Cell): Degree {
  const { rel, ring } = parseCell(cell)
  return spell(rel, ring).degree
}

/** The cell a numeral names: `♭VI` → `8:maj`, `vii°` → `11:dim`, `♯iv°` → `6:dim`, `i` → `0:min`. */
export function cellFromNumeral(numeral: string): Cell {
  const m = /^([♭♯]?)([IViv]+)(°)?$/.exec(numeral)
  if (!m) throw new Error(`not a numeral: ${numeral}`)
  const acc = m[1] === '♭' ? -1 : m[1] === '♯' ? 1 : 0
  const degree = romanDegree(m[2]!)
  if (degree === undefined) throw new Error(`bad numeral in ${numeral}`)
  const rel = (NATURAL[degree]! + acc + 12) % 12
  const ring: Ring = m[3] ? 'dim' : m[2] === m[2]!.toUpperCase() ? 'maj' : 'min'
  return cellId(rel, ring)
}

/**
 * The two columns (spec §5): the stars each observatory opens at each night, for the songs of its
 * family. Before dawn opens, in each, every star its column has not yet named, so that at dawn the
 * two atlases are the whole sky.
 */
const COLUMN_ROWS: Record<Observatory, readonly (readonly string[])[]> = {
  major: [
    ['I', 'V'],
    ['IV'],
    ['vi'],
    ['ii', 'iii', 'vii°'],
    // Midnight: the parallel minor's chords, and Mixolydian's iii°
    ['i', 'iv', 'v', '♭III', '♭VI', '♭VII', 'ii°', 'iii°'],
    // Small hours: the applied dominants (V/V, V/vi, V/ii, V/iii) and Lydian's vii
    ['II', 'III', 'VI', 'VII', 'vii'],
    // Before dawn: the half-step neighbours, then the rest of the sky
    ['♭II', '♯i°', '♯ii°', '♯iv°', '♯v°'],
  ],
  minor: [
    ['i', '♭VI', '♭VII'],
    ['♭III'],
    ['iv'],
    ['v', 'ii°'],
    // Midnight: the major dominant and its leading-tone chord
    ['V', 'vii°'],
    // Small hours: the applied dominants (V/iv, V/♭VII, V/v) and Dorian's ii
    ['I', 'IV', 'II', 'ii'],
    // Before dawn: the half-step neighbours, Phrygian's v° and ♭vii, then the rest of the sky
    ['♭II', 'III', 'vi°', 'v°', '♭vii'],
  ],
}

const COLUMNS: Record<Observatory, readonly (readonly Cell[])[]> = {
  major: withRest(COLUMN_ROWS.major),
  minor: withRest(COLUMN_ROWS.minor),
}

function withRest(rows: readonly (readonly string[])[]): (readonly Cell[])[] {
  const out = rows.map((r) => r.map(cellFromNumeral))
  const named = new Set(out.flat())
  const rest = STARS.filter((c) => !named.has(c))
  out[6] = [...out[6]!, ...rest]
  return out
}

/** The stars an observatory's night `n` opens, in the order they come out (spec §5, §8). */
export function levelAdds(o: Observatory, level: Level): readonly Cell[] {
  return COLUMNS[o][level - 1]!
}

/** `open(o, n)`: the stars the observatory has opened by night `n` (spec §5). */
export function openCells(o: Observatory, level: Level): ReadonlySet<Cell> {
  const out = new Set<Cell>()
  for (let n = 1; n <= level; n++) for (const c of COLUMNS[o][n - 1]!) out.add(c)
  return out
}

/** The night at which the observatory opens a cell, or null for a dark cell. */
export function nightOf(o: Observatory, cell: Cell): Level | null {
  if (!isStar(cell)) return null
  for (let n = 1; n <= 7; n++) if (COLUMNS[o][n - 1]!.includes(cell)) return n as Level
  return null
}

export function isOpen(o: Observatory, level: Level, cell: Cell): boolean {
  const n = nightOf(o, cell)
  return n !== null && n <= level
}
